import type { Table } from 'dexie';
import type { IRepository } from './IRepository';

/**
 * Implementation generique du Repository pattern sur Dexie.
 */
export abstract class BaseRepository<T extends { id: string }> implements IRepository<T> {
  constructor(protected readonly table: Table<T, string>) {}

  async findById(id: string): Promise<T | null> {
    const result = await this.table.get(id);
    return result ?? null;
  }
  async findAll(): Promise<T[]> {
    return this.table.toArray();
  }
  async findBy(criteria: Partial<T>): Promise<T[]> {
    return this.table
      .filter((entity: T) =>
        Object.entries(criteria).every(
          ([key, value]) => (entity as Record<string, unknown>)[key] === value,
        ),
      )
      .toArray();
  }
  async save(entity: T): Promise<T> {
    await this.table.put(entity);
    return entity;
  }
  async saveMany(entities: T[]): Promise<T[]> {
    await this.table.bulkPut(entities);
    return entities;
  }
  async delete(id: string): Promise<void> {
    await this.table.delete(id);
  }
  async count(criteria?: Partial<T>): Promise<number> {
    if (!criteria) return this.table.count();
    const results = await this.findBy(criteria);
    return results.length;
  }
  // Alias
  async get(id: string): Promise<T | undefined> {
    const r = await this.table.get(id);
    return r ?? undefined;
  }
  async list(): Promise<T[]> {
    return this.findAll();
  }
  async add(entity: T): Promise<void> {
    await this.table.put(entity);
  }
  async update(entity: T): Promise<void> {
    await this.table.put(entity);
  }
}
